/**
 * Fetch wrapper for the Supabase client.
 * A WKWebView connection can sit 17–21s between the CORS preflight and the
 * real response. Abort that wait. Retry once only when doing it again cannot
 * insert a second row.
 * Streaming responses are left alone once headers arrive, so a long AI or
 * upload body is not cut off.
 */

const {
  isConnectionFailure,
  connectionError,
} = require('./connectionCopy');

const DEFAULT_TIMEOUT_MS = 12000;
const LONG_TIMEOUT_MS = 120000;
const RETRY_BACKOFF_MS = 2000;
const RETRY_NOTICE_GAP_MS = 3000;

let retryNotifier = null;
let lastRetryNotice = 0;

function setNetworkRetryNotifier(notify) {
  retryNotifier = typeof notify === 'function' ? notify : null;
}

function notifyRetry(now) {
  const clock = typeof now === 'number' ? now : Date.now();
  if (clock - lastRetryNotice < RETRY_NOTICE_GAP_MS) return false;
  lastRetryNotice = clock;
  if (retryNotifier) retryNotifier();
  return true;
}

function requestUrl(input) {
  if (typeof input === 'string') return input;
  if (input && typeof input.url === 'string') return input.url;
  return '';
}

function requestMethod(input, init) {
  const method = (init && init.method) || (input && input.method) || 'GET';
  return String(method).toUpperCase();
}

function isStorageUpload(url, method) {
  if (method !== 'POST' && method !== 'PUT') return false;
  if (!/\/storage\/v1\/object\//.test(url)) return false;
  if (/\/storage\/v1\/object\/(?:sign|list|public|info|authenticated)\b/.test(url)) return false;
  return true;
}

function isEdgeFunction(url) {
  return /\/functions\/v1\//.test(url);
}

function headerValue(init, name) {
  const headers = init && init.headers;
  if (!headers) return '';
  if (typeof headers.get === 'function') return headers.get(name) || '';
  const found = Object.keys(headers).find((key) => key.toLowerCase() === name);
  return found ? headers[found] : '';
}

function isStreamingBody(init) {
  if (!init || init.body == null) return false;
  if (typeof ReadableStream !== 'undefined' && init.body instanceof ReadableStream) return true;
  return false;
}

function chooseTimeoutMs(input, init) {
  const url = requestUrl(input);
  const method = requestMethod(input, init);
  if (
    isStorageUpload(url, method)
    || isEdgeFunction(url)
    || /text\/event-stream/i.test(String(headerValue(init, 'accept')))
    || isStreamingBody(init)
  ) {
    return LONG_TIMEOUT_MS;
  }
  return DEFAULT_TIMEOUT_MS;
}

function isIdempotentRequest(input, init) {
  const method = requestMethod(input, init);
  if (method === 'GET' || method === 'HEAD' || method === 'OPTIONS') return true;
  if (method === 'POST' && /\/auth\/v1\/token(?:[/?#]|$)/.test(requestUrl(input))) return true;
  return false;
}

function isNetworkFailure(error) {
  if (!error) return false;
  if (isConnectionFailure(error)) return true;
  return error.name === 'TypeError';
}

function parentAborted(init) {
  return Boolean(init && init.signal && init.signal.aborted);
}

function abortQuietly(controller) {
  try {
    controller.abort();
  } catch (error) {
    // Already aborted, or this WebView's AbortController takes no reason.
  }
}

function linkAbort(parent, controller) {
  if (!parent) return function unlink() {};
  if (parent.aborted) {
    abortQuietly(controller);
    return function unlink() {};
  }
  const onAbort = () => abortQuietly(controller);
  parent.addEventListener('abort', onAbort, { once: true });
  return function unlink() {
    parent.removeEventListener('abort', onAbort);
  };
}

async function attemptFetch(baseFetch, input, init, timeoutMs) {
  const controller = new AbortController();
  const unlink = linkAbort(init && init.signal, controller);
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    abortQuietly(controller);
  }, timeoutMs);
  try {
    const response = await baseFetch(input, Object.assign({}, init, { signal: controller.signal }));
    // Headers are in. Clear the timer so a stream or a JSON body can finish.
    clearTimeout(timer);
    return response;
  } catch (error) {
    clearTimeout(timer);
    if (timedOut && !parentAborted(init)) {
      const timeout = new Error('timeout');
      timeout.name = 'TimeoutError';
      timeout.cause = error;
      throw timeout;
    }
    throw error;
  } finally {
    unlink();
  }
}

function createTimedFetch(deps) {
  const baseFetch = (deps && deps.fetch) || function browserFetch(input, init) {
    return window.fetch(input, init);
  };
  const sleep = (deps && deps.sleep) || ((ms) => new Promise((resolve) => setTimeout(resolve, ms)));
  const now = (deps && deps.now) || (() => Date.now());
  const notify = (deps && deps.notify) || notifyRetry;

  return async function timedFetch(input, init, options) {
    const settings = options || {};
    const policy = settings.retry || 'idempotent';
    const timeoutMs = settings.timeoutMs || chooseTimeoutMs(input, init);
    const backoffMs = settings.backoffMs != null ? settings.backoffMs : RETRY_BACKOFF_MS;
    const maxAttempts = policy === 'never' ? 1 : 2;
    let lastError = null;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        return await attemptFetch(baseFetch, input, init || {}, timeoutMs);
      } catch (error) {
        lastError = error;
        if (parentAborted(init)) throw error;
        const allow = attempt < maxAttempts
          && isNetworkFailure(error)
          && (policy === 'once' || (policy === 'idempotent' && isIdempotentRequest(input, init)));
        if (!allow) break;
        notify(now());
        if (backoffMs > 0) await sleep(backoffMs);
      }
    }
    if (isNetworkFailure(lastError)) throw connectionError(lastError);
    throw lastError;
  };
}

const timedFetch = createTimedFetch();

function postAssistant(url, init) {
  return timedFetch(url, init, { timeoutMs: LONG_TIMEOUT_MS, retry: 'once' });
}

module.exports = {
  DEFAULT_TIMEOUT_MS,
  LONG_TIMEOUT_MS,
  RETRY_BACKOFF_MS,
  chooseTimeoutMs,
  isIdempotentRequest,
  createTimedFetch,
  timedFetch,
  postAssistant,
  setNetworkRetryNotifier,
  notifyRetry,
};
